// Company-growth illustration: a hand-coded SVG skyline that builds up as
// view.stage advances (garage → angel → vc → growth → ipo → public).
// Headcount modulates one secondary detail — how many windows are lit —
// rather than driving the scene itself; the six scenes are what read as
// "the company grew."

import type { CompanyStage } from "../../engine/types.js";

interface StageArt {
  roofline: "peaked" | "flat" | "crane" | "flag";
  floors: number;
  bays: number;
  width: number;
  second?: boolean; // a smaller companion building beside the main one
}

const STAGE_ART: Record<CompanyStage, StageArt> = {
  garage: { roofline: "peaked", floors: 1, bays: 1, width: 46 },
  angel: { roofline: "flat", floors: 1, bays: 3, width: 78 },
  vc: { roofline: "flat", floors: 3, bays: 3, width: 66 },
  growth: { roofline: "flat", floors: 5, bays: 4, width: 74, second: true },
  ipo: { roofline: "crane", floors: 8, bays: 4, width: 60 },
  public: { roofline: "flag", floors: 12, bays: 5, width: 70, second: true },
};

const FLOOR_H = 9;
const WIN = 5;
const BASELINE = 78;
const CANVAS_W = 220;
const CANVAS_H = 92;

/** Ground-floor bay is a garage door, not a window, for the "peaked" stage. */
function groundFloorSkipped(art: StageArt): number {
  return art.roofline === "peaked" ? 1 : 0;
}

/** Which (floor, bay) windows are lit — filled bottom-up, so the ground
 *  floor lights first, the way an office actually fills at dusk. */
function litWindowSet(art: StageArt, litCount: number): Set<string> {
  const set = new Set<string>();
  let remaining = litCount;
  const startFloor = groundFloorSkipped(art);
  for (let f = art.floors - 1; f >= startFloor && remaining > 0; f--) {
    for (let b = 0; b < art.bays && remaining > 0; b++) {
      set.add(`${f}-${b}`);
      remaining--;
    }
  }
  return set;
}

function Building({ x, art, lit }: { x: number; art: StageArt; lit: Set<string> }) {
  const h = art.floors * FLOOR_H;
  const top = BASELINE - h;
  const left = x - art.width / 2;
  const startFloor = groundFloorSkipped(art);
  const bayGap = art.width / (art.bays + 1);

  const windows = [];
  for (let f = startFloor; f < art.floors; f++) {
    for (let b = 0; b < art.bays; b++) {
      const isLit = lit.has(`${f}-${b}`);
      windows.push(
        <rect
          key={`${f}-${b}`}
          x={left + bayGap * (b + 1) - WIN / 2}
          y={top + FLOOR_H * f + (FLOOR_H - WIN) / 2}
          width={WIN}
          height={WIN}
          fill={isLit ? "var(--ink)" : "var(--bg)"}
          fillOpacity={isLit ? 0.85 : 1}
          stroke="var(--ink-dim)"
          strokeWidth={0.6}
        />,
      );
    }
  }

  return (
    <g>
      <rect x={left} y={top} width={art.width} height={h} fill="var(--bg-panel)" stroke="var(--border-bright)" strokeWidth={1} />
      {art.roofline === "peaked" && (
        <>
          <polygon points={`${left - 4},${top} ${x},${top - 16} ${left + art.width + 4},${top}`} fill="var(--bg-panel)" stroke="var(--border-bright)" />
          <rect x={x - art.width * 0.28} y={BASELINE - FLOOR_H} width={art.width * 0.56} height={FLOOR_H} fill="var(--bg-raised)" stroke="var(--border-bright)" strokeWidth={0.6} />
        </>
      )}
      {art.roofline === "crane" && (
        <g stroke="var(--ink-faint)" strokeWidth={1} fill="none">
          <line x1={left + art.width * 0.78} y1={top} x2={left + art.width * 0.78} y2={top - 26} />
          <line x1={left + art.width * 0.5} y1={top - 24} x2={left + art.width + 10} y2={top - 24} />
          <line x1={left + art.width + 10} y1={top - 24} x2={left + art.width + 10} y2={top - 12} strokeDasharray="1,2" />
          <rect x={left + art.width * 0.46} y={top - 27} width={6} height={5} fill="var(--ink-faint)" stroke="none" />
        </g>
      )}
      {art.roofline === "flag" && (
        <g>
          <line x1={x} y1={top} x2={x} y2={top - 16} stroke="var(--ink-dim)" strokeWidth={1} />
          <polygon points={`${x},${top - 16} ${x + 12},${top - 12} ${x},${top - 8}`} fill="var(--cyan)" />
        </g>
      )}
      {windows}
    </g>
  );
}

export function CompanyIllustration({ stage, headcount }: { stage: CompanyStage; headcount: number }) {
  const art = STAGE_ART[stage];
  const totalWindows = (art.floors - groundFloorSkipped(art)) * art.bays;
  const litCount = totalWindows === 0 ? 0 : Math.max(headcount > 0 ? 1 : 0, Math.min(totalWindows, Math.round(headcount / 4)));
  const lit = litWindowSet(art, litCount);
  const mainX = art.second ? CANVAS_W / 2 + 24 : CANVAS_W / 2;

  return (
    <svg width={CANVAS_W} height={CANVAS_H}>
      <line x1={4} x2={CANVAS_W - 4} y1={BASELINE} y2={BASELINE} stroke="var(--border)" />
      {art.second && (
        <rect
          x={mainX - art.width / 2 - 34}
          y={BASELINE - (art.floors - 3) * FLOOR_H}
          width={26}
          height={(art.floors - 3) * FLOOR_H}
          fill="var(--bg-panel)"
          stroke="var(--border)"
          strokeWidth={1}
        />
      )}
      <Building x={mainX} art={art} lit={lit} />
    </svg>
  );
}
